import { useState } from 'react';
import { User } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * ProfilePicture component - Displays a user's profile picture with fallback silhouette
 * 
 * @param {string} src - URL of the profile picture
 * @param {string} alt - Alt text for the image
 * @param {string} size - Size variant: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 * @param {string} className - Additional CSS classes
 * @param {function} onClick - Click handler
 */
const ProfilePicture = ({ 
  src, 
  alt = 'Profile picture', 
  size = 'md',
  className = '',
  onClick = null 
}) => {
  const [imageError, setImageError] = useState(false);
  
  // Size mappings
  const sizeClasses = {
    xs: 'w-6 h-6',
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
    '2xl': 'w-20 h-20'
  };
  
  const iconSizes = {
    xs: 'w-4 h-4',
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-7 h-7',
    xl: 'w-10 h-10',
    '2xl': 'w-12 h-12'
  };
  
  const containerSize = sizeClasses[size] || sizeClasses.md;
  const iconSize = iconSizes[size] || iconSizes.md;
  
  const handleImageError = () => {
    setImageError(true);
  };
  
  const showFallback = !src || imageError;
  
  const baseClasses = cn(
    'rounded-full overflow-hidden flex-shrink-0',
    containerSize,
    onClick && 'cursor-pointer hover:ring-2 hover:ring-cyan-400 transition-all',
    className
  );
  
  if (showFallback) {
    return (
      <div 
        className={cn(baseClasses, 'bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center')}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        aria-label={alt}
      >
        <User className={cn(iconSize, 'text-slate-300')} />
      </div>
    );
  }
  
  return (
    <div 
      className={baseClasses}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        onError={handleImageError}
      />
    </div>
  );
};

export default ProfilePicture;
